import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

export type SectionTab<T extends string> = {
  id: T
  label: string
  status?: ReactNode
}

export function useSectionTab<T extends string>(tabs: readonly SectionTab<T>[], defaultTab: T) {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const activeTab = tabs.some((tab) => tab.id === requested) ? requested as T : defaultTab

  const setActiveTab = (tab: T) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  return [activeTab, setActiveTab] as const
}

export function SectionTabs<T extends string>({
  tabs,
  activeTab,
  onSelect,
  label,
  idPrefix,
}: {
  tabs: readonly SectionTab<T>[]
  activeTab: T
  onSelect: (tab: T) => void
  label: string
  idPrefix: string
}) {
  const tabListRef = useRef<HTMLDivElement>(null)

  const selectAdjacentTab = (event: KeyboardEvent<HTMLButtonElement>, currentTab: T) => {
    const currentIndex = tabs.findIndex((tab) => tab.id === currentTab)
    let nextIndex = currentIndex
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = tabs.length - 1
    else return

    event.preventDefault()
    const nextTab = tabs[nextIndex]?.id
    if (!nextTab) return
    onSelect(nextTab)
    tabListRef.current?.querySelector<HTMLButtonElement>(`#${idPrefix}-tab-${nextTab}`)?.focus()
  }

  return (
    <div className="section-tabs" role="tablist" aria-label={label} ref={tabListRef}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          id={`${idPrefix}-tab-${tab.id}`}
          className="section-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`${idPrefix}-panel-${tab.id}`}
          tabIndex={activeTab === tab.id ? 0 : -1}
          onClick={() => onSelect(tab.id)}
          onKeyDown={(event) => selectAdjacentTab(event, tab.id)}
        >
          <span>{tab.label}</span>
          {tab.status}
        </button>
      ))}
    </div>
  )
}

export function SectionTabPanel<T extends string>({
  idPrefix,
  tab,
  activeTab,
  children,
}: {
  idPrefix: string
  tab: T
  activeTab: T
  children: ReactNode
}) {
  return (
    <div
      id={`${idPrefix}-panel-${tab}`}
      className="section-tab-panel"
      role="tabpanel"
      aria-labelledby={`${idPrefix}-tab-${tab}`}
      tabIndex={0}
      hidden={activeTab !== tab}
    >
      {children}
    </div>
  )
}
